import { type EntityType } from 'core/domain/EntityType'
import { type FullMode } from 'core/domain/Mode'
import { createDate } from 'core/utils/date'

import { type BulkRate } from '../domain/BulkRate'
import { type BulkRateAvailability } from '../domain/BulkRateAvailability'
import { type BulkSpotQuote } from '../domain/BulkSpotQuote'
import { type Shipper } from '../domain/Shipper'
import { type SpotQuoteStatus } from '../domain/SpotQuoteStatus'
import {
  mapApiToQuote,
  type RawQuote,
  mapApiToRateLine,
  type RawRateLine,
  type RawSubmitSpotQuoteParams,
  type SubmitSpotQuoteParams,
  mapSpotQuoteToApi,
} from './SpotQuoteMapper'

export type SubmitBulkSpotQuoteParams = {
  inviteUuid: string
  rates: Array<SubmitSpotQuoteParams>
}

type RawSubmitBulkSpotQuoteParams = {
  rates: Array<
    RawSubmitSpotQuoteParams & {
      rate_uuid: string
    }
  >
}

type RawShipper = {
  company_name: string
  email: string
  id: number
  logo_email_url: string | null
  phone_number: string | null
}

type RawBulkRateAvailability = {
  is_bid_available: boolean
  reason: string | null
}

type RawBulkRate = {
  availability: RawBulkRateAvailability
  carrier_notes: string | null
  flat_rate: string | null
  per_mile_rate: number | null
  quote: RawQuote
  status: SpotQuoteStatus
  uuid: string
  ratelines?: Array<RawRateLine>
  book_now_enabled?: boolean
  submitted_at: string
  created_at: string
}

export type RawBulkCarrier = {
  name: string
  email: string
  entity_type?: EntityType
  id: number
  dot?: string
  scac?: string
  mc?: string
}

export type RawBulkSpotQuote = {
  all_quotes_expired: boolean
  all_rates_sent: boolean
  carrier: RawBulkCarrier
  carrier_name: string
  carrier_email: string
  mode: FullMode
  number_shipments: number
  rates: Array<RawBulkRate>
  shipper: RawShipper
  soonest_bid_deadline?: string
}

function mapApiToBulkRateAvailability(
  rawBulkRateAvailability: RawBulkRateAvailability
): BulkRateAvailability {
  return {
    isBidAvailable: rawBulkRateAvailability.is_bid_available,
    reason: rawBulkRateAvailability.reason || undefined,
  }
}

function mapApiToBulkRate(rawBulkRate: RawBulkRate): BulkRate {
  const ratelines = (rawBulkRate.ratelines ?? []).map(mapApiToRateLine)

  return {
    availability: mapApiToBulkRateAvailability(rawBulkRate.availability),
    carrierNotes: rawBulkRate.carrier_notes || undefined,
    flatRate: rawBulkRate.flat_rate || undefined,
    perMileRate: rawBulkRate.per_mile_rate ?? undefined,
    quote: mapApiToQuote(rawBulkRate.quote),
    status: rawBulkRate.status,
    uuid: rawBulkRate.uuid,
    bookNowEnabled: rawBulkRate.book_now_enabled,
    ratelines,
    submittedAt: createDate(rawBulkRate.submitted_at),
    createdAt: new Date(rawBulkRate.created_at),
  }
}

function mapApiToShipperInformation(rawShipper: RawShipper): Shipper {
  return {
    email: rawShipper.email,
    id: rawShipper.id,
    logoUrl: rawShipper.logo_email_url || undefined,
    companyName: rawShipper.company_name,
    phoneNumber: rawShipper.phone_number || undefined,
  }
}

function mapApiToBulkCarrier(carrier: RawBulkCarrier) {
  return {
    name: carrier.name,
    email: carrier.email,
    entityType: carrier?.entity_type,
    id: carrier.id,
    dot: carrier?.dot,
    scac: carrier?.scac,
    mc: carrier?.mc,
  }
}

export function mapApiToBulkSpotQuote(rawBulkSpotQuote: RawBulkSpotQuote): BulkSpotQuote {
  return {
    allQuotesExpired: rawBulkSpotQuote.all_quotes_expired,
    allRatesSent: rawBulkSpotQuote.all_rates_sent,
    carrier: mapApiToBulkCarrier(rawBulkSpotQuote.carrier),
    carrierName: rawBulkSpotQuote.carrier_name,
    carrierEmail: rawBulkSpotQuote.carrier_email,
    mode: rawBulkSpotQuote.mode,
    numberShipments: rawBulkSpotQuote.number_shipments,
    rates: rawBulkSpotQuote.rates.map(mapApiToBulkRate),
    shipper: mapApiToShipperInformation(rawBulkSpotQuote.shipper),
    soonestBidDeadline: createDate(rawBulkSpotQuote.soonest_bid_deadline),
  }
}

export function mapBulkSpotQuoteToApi(
  bulkSpotQuote: SubmitBulkSpotQuoteParams
): RawSubmitBulkSpotQuoteParams {
  return {
    rates: bulkSpotQuote.rates.map(rate => ({
      rate_uuid: rate.id,
      ...mapSpotQuoteToApi(rate),
    })),
  }
}
